import { Icons } from '@/assets/icons/icons';
import { ActiveDot } from '@/components/ui/layout/active-dot';
import { cn } from '@/lib/utils';

import { FeedAge } from './FeedAge';
import { ctaIconOf } from './feedCta';
import { FeedIcon } from './FeedIcon';
import { FeedIconCheck } from './FeedIconCheck';
import { capitalized, feedKindOf, TODO_KIND } from './feedKind';
import { FeedOpenLink } from './FeedOpenLink';
import { FeedOutcome } from './FeedOutcome';
import { FeedRowAction } from './FeedRowActions';
import { FeedRowTitle } from './FeedRowTitle';
import { FeedRunningPill } from './FeedRunningPill';
import { snoozeWhenLabel } from './feedSnooze';
import {
  FEED_CHIP_CLASS,
  FEED_CTA_CLASS,
  FEED_DESCRIPTION_CLASS,
  FEED_FADED_TEXT_CLASS,
  FEED_ROW_GRID,
  FEED_ROW_LINE_CLASS,
  FEED_ROW_META_CLASS,
  FEED_ROW_SURFACE_CLASS,
  STAKE_PILL_CLASS,
} from './feedStyles';
import { FeedSubtasksChip } from './FeedSubtasksChip';
import { useRowCheck } from './useRowCheck';

import type { FeedPrimaryPress, FeedRowView } from './feedRowView';
import type { ChildProgress } from '@littlebird/action-cards';
import type { KeyboardEvent, ReactNode } from 'react';

// The row's tick, Dismiss and ⋯ take the age's place while the row is hovered or its menu is open
const AGE_HIDES_CLASS =
  'group-hover/feed-row:hidden group-has-[:focus-visible]/feed-row:hidden group-has-[[data-state=open]]/feed-row:hidden';

const MORE_SHOWS_CLASS =
  'hidden group-hover/feed-row:flex group-has-[:focus-visible]/feed-row:flex has-[[data-state=open]]:flex';

export type FeedRowProps = {
  view: FeedRowView;
  icon: ReactNode;
  todo?: boolean;
  struck?: boolean;
  description?: string;
  appName: string;
  onOpen?: () => void;
  onDismiss?: () => void;
  onChat?: () => void;
  chatLabel?: string;
  // The user started a chat from the card, so its Chat button continues that chat
  chatStarted?: boolean;
  // The row's Pin, Snooze and ⋯, shown in the age's place on hover
  hoverActions?: ReactNode;
  // What the row is, such as a chat's status; a card's row shows only its title
  label?: string;
  labelDot?: boolean;
  labelAccessory?: ReactNode;
  // A note only staff see, such as the backend that made the card; shown after the label or the outcome
  staffLabel?: ReactNode;
  backAt?: Date;
  // Checks the row off from its icon: marks a to-do or a draft done, or an update read
  onMarkDone?: () => unknown;
  // `box` is a to-do's or a draft's checkbox; `mark` is an update's app mark
  checkKind?: 'box' | 'mark';
  // What checking the row off does, in its icon's tooltip
  checkLabel?: string;
  // An update shows nothing on hover; it clears from its icon, and the open card holds the rest
  hoverless?: boolean;
  // The watcher's suggestion, shown on the row's first line
  watchSuggestion?: ReactNode;
  // The cards under this one, counted on the row
  subtasks?: ChildProgress;
  onPress: (press: FeedPrimaryPress) => void;
  onOpenUrl: (url: string) => void;
  onKeyDown?: (event: KeyboardEvent<HTMLElement>) => void;
};

export const FeedRow = ({
  view,
  icon,
  todo = false,
  struck = false,
  description,
  appName,
  onOpen,
  onDismiss,
  hoverActions,
  label,
  labelDot,
  labelAccessory,
  staffLabel,
  backAt,
  onMarkDone,
  checkKind = 'box',
  checkLabel = 'Mark done',
  hoverless = false,
  watchSuggestion,
  subtasks,
  onPress,
  onOpenUrl,
  onKeyDown,
}: FeedRowProps) => {
  const { pill, outcome, stake } = view;
  const kind = todo ? TODO_KIND : feedKindOf(view.verb);
  const CtaIcon = pill ? ctaIconOf(pill, kind.icon) : null;
  const actionable = !outcome && !view.running;
  const openLink =
    outcome?.tone === 'done' && view.openUrl ? (
      <FeedOpenLink
        url={view.openUrl}
        appName={appName}
        onOpenUrl={onOpenUrl}
      />
    ) : null;
  const { checking, check } = useRowCheck(onMarkDone);
  // The row keeps one labelled button, its own primary, under the description
  const primary = pill && CtaIcon && pill.press.kind !== 'acknowledge';
  const hasActions = actionable && Boolean(primary);
  // The row's icon checks it off, so hover shows Dismiss, Pin, Snooze and ⋯
  const hoverButtons =
    actionable && !hoverless && !checking && (onDismiss || hoverActions) ? (
      <>
        {onDismiss ? (
          <FeedRowAction
            icon={Icons.trash}
            label="Dismiss"
            accessibleLabel={`Dismiss ${view.title}`}
            hotkey={['mod', 'backspace']}
            onClick={onDismiss}
          />
        ) : null}
        {hoverActions}
      </>
    ) : null;
  const showsMore = Boolean(hoverButtons);

  return (
    <div
      data-testid="feed-row"
      className={cn(
        FEED_ROW_GRID,
        FEED_ROW_SURFACE_CLASS,
        (onOpen || hasActions) && 'hover:bg-surface-hover'
      )}
    >
      <span className="mt-[calc(2*var(--ac-u))]">
        {actionable && onMarkDone ? (
          <FeedIconCheck
            title={view.title}
            kind={checkKind}
            label={checkLabel}
            failed={view.failed}
            ticked={checking}
            onCheck={check ?? onMarkDone}
          >
            {icon}
          </FeedIconCheck>
        ) : (
          <FeedIcon failed={view.failed}>{icon}</FeedIcon>
        )}
      </span>
      <span className="flex min-w-0 flex-col">
        <span className={FEED_ROW_LINE_CLASS}>
          <FeedRowTitle
            title={todo ? capitalized(view.title) : view.title}
            struck={struck}
            striking={checking}
            onOpen={onOpen}
            onKeyDown={onKeyDown}
          />
          {outcome ? (
            <FeedOutcome outcome={outcome} />
          ) : (
            <>
              {label || labelDot ? (
                <span
                  data-testid="feed-row-meta"
                  className={FEED_ROW_META_CLASS}
                >
                  {labelDot ? (
                    <ActiveDot className="mr-[calc(4*var(--ac-u))] inline-block bg-blue-500 align-middle" />
                  ) : null}
                  {label}
                </span>
              ) : null}
              {labelAccessory}
              {watchSuggestion}
              {stake && !openLink ? (
                <span
                  className={cn(
                    FEED_CHIP_CLASS,
                    'min-w-0 shrink-[10] truncate',
                    STAKE_PILL_CLASS[stake.tone]
                  )}
                >
                  {stake.label}
                </span>
              ) : null}
            </>
          )}
          {staffLabel}
          {subtasks ? <FeedSubtasksChip progress={subtasks} /> : null}
        </span>
        {description ? (
          <span
            className={cn(FEED_DESCRIPTION_CLASS, 'mt-[calc(2*var(--ac-u))]')}
          >
            {description}
          </span>
        ) : null}
        {hasActions && pill && CtaIcon ? (
          <span className="pointer-events-none relative z-[1] mt-[calc(12*var(--ac-u))] flex items-center gap-[calc(4*var(--ac-u))]">
            <button
              type="button"
              aria-label={`${pill.label} ${view.title}`}
              className={FEED_CTA_CLASS}
              onClick={() => onPress(pill.press)}
            >
              <CtaIcon className="size-[calc(12*var(--ac-u))]" />
              {pill.label}
            </button>
          </span>
        ) : null}
      </span>
      <span className="relative z-[1] flex h-[var(--ac-row-line-h)] items-center gap-[calc(6*var(--ac-u))]">
        {outcome ? null : (
          <span
            className={cn(
              'shrink-0 whitespace-nowrap text-[length:calc(11.5*var(--ac-u))]',
              FEED_FADED_TEXT_CLASS,
              showsMore && AGE_HIDES_CLASS
            )}
          >
            {backAt ? (
              `Back ${snoozeWhenLabel(backAt, new Date())}`
            ) : (
              <FeedAge at={view.age} />
            )}
          </span>
        )}
        {showsMore ? (
          <span
            className={cn(
              '-mr-[calc(4*var(--ac-u))] items-center gap-[calc(2*var(--ac-u))]',
              MORE_SHOWS_CLASS
            )}
          >
            {hoverButtons}
          </span>
        ) : null}
        {openLink}
        {pill && view.running ? (
          <FeedRunningPill pill={pill} onPress={onPress} />
        ) : null}
      </span>
    </div>
  );
};
